import React, { useState } from "react";
import { NavLink as RouterNavLink } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Collapse,
  Container,
  Navbar,
  NavbarToggler,
  NavbarBrand,
  Nav,
  NavItem,
  NavLink,
  Button,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";

import { useAuth0 } from "@auth0/auth0-react";
import { useAuth } from "../AuthenticatedApp";
import { useRequestsContext } from "../context/RequestsContext";
import { useVenueAlertSound } from "../context/VenueAlertSoundContext";
import { getDefaultRoute } from "../utils/history";
import garconLogo from "../assets/garcon_icon_notext.png";

const NavBar = ({ visible = true }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { notificationCount } = useRequestsContext();
  const { soundEnabled, playbackBlocked, toggleSoundEnabled, primeSound } =
    useVenueAlertSound();
  const { user, isAuthenticated, loginWithRedirect, logout } = useAuth0();
  const { userPermissions } = useAuth();
  const permissionNames = userPermissions || [];

  const toggle = () => setIsOpen((current) => !current);
  const closeMobilePanel = () => setIsOpen(false);
  const canAccessTables = permissionNames.includes("view_tables") || permissionNames.includes("edit_tables");
  const canAccessRequests = permissionNames.includes("view_requests") || permissionNames.includes("edit_requests");

  const defaultRoute = getDefaultRoute(permissionNames);

  const logoutWithRedirect = () =>
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });

  const handleSoundControl = async () => {
    if (!soundEnabled) {
      toggleSoundEnabled();
      return;
    }

    if (playbackBlocked) {
      await primeSound();
      return;
    }

    toggleSoundEnabled();
  };

  const soundButtonTitle = !soundEnabled
    ? "Bildirim sesini aç"
    : playbackBlocked
      ? "Bildirim sesini yeniden etkinleştir"
      : "Bildirim sesini kapat";

  const requestBadge =
    notificationCount > 0 ? (
      <span className="venue-nav-badge">{notificationCount}</span>
    ) : null;

  const renderLink = (to, label, extra = null, isMobile = false) => {
    const className = isMobile ? "venue-mobile-link" : "venue-nav-link";
    return (
      <NavLink
        tag={RouterNavLink}
        to={to}
        exact
        className={className}
        activeClassName="active"
        onClick={isMobile ? closeMobilePanel : undefined}
      >
        {label}
        {extra}
      </NavLink>
    );
  };

  return (
    <div className={`venue-topbar ${visible ? 'venue-topbar--visible' : ''}`}>
      <Navbar color="light" light expand="xl" container={false}>
        <Container fluid>
          <NavbarBrand
            tag={RouterNavLink}
            to={defaultRoute}
            className="venue-brand"
            onClick={closeMobilePanel}
          >
            <span className="venue-brand__mark">
              <img src={garconLogo} alt="Garcon" />
            </span>
            <span className="venue-brand__text">
              <span className="venue-brand__eyebrow">Venue</span>
              <span className="venue-brand__title">Garcon Operasyon</span>
            </span>
          </NavbarBrand>
          <NavbarToggler onClick={toggle} />
          <Collapse isOpen={isOpen} navbar>
            <Nav className="venue-nav-links d-none d-xl-flex mr-auto" navbar>
              {isAuthenticated && permissionNames.includes("manage_shift") && (
                <NavItem>{renderLink("/orders", "Siparişler")}</NavItem>
              )}
              {isAuthenticated && canAccessTables && (
                <NavItem>{renderLink("/tables", "Masalar")}</NavItem>
              )}
              {isAuthenticated && canAccessRequests && (
                <NavItem>{renderLink("/requests", "Talepler", requestBadge)}</NavItem>
              )}
              {isAuthenticated && permissionNames.includes("take_payment") && (
                <NavItem>{renderLink("/cashier", "Kasa")}</NavItem>
              )}
              {isAuthenticated && permissionNames.includes("manage_shift") && (
                <NavItem>{renderLink("/shift", "Vardiya")}</NavItem>
              )}
            </Nav>

            <Nav className="venue-toolbar ml-auto d-none d-xl-flex" navbar>
              {isAuthenticated ? (
                <NavItem className="d-flex align-items-center">
                  <Button
                    id="venueSoundToggleBtn"
                    color="link"
                    className="sound-toggle-button venue-icon-button position-relative"
                    onClick={handleSoundControl}
                    title={soundButtonTitle}
                    aria-label={soundButtonTitle}
                  >
                    <FontAwesomeIcon
                      icon={soundEnabled ? "volume-up" : "volume-mute"}
                    />
                    {soundEnabled && playbackBlocked ? (
                      <span className="sound-toggle-indicator" />
                    ) : null}
                  </Button>
                </NavItem>
              ) : null}

              {!isAuthenticated ? (
                <NavItem>
                  <Button
                    id="qsLoginBtn"
                    color="primary"
                    onClick={() => loginWithRedirect()}
                  >
                    Giriş Yap
                  </Button>
                </NavItem>
              ) : null}

              {isAuthenticated ? (
                <UncontrolledDropdown nav inNavbar>
                  <DropdownToggle
                    nav
                    caret
                    id="profileDropDown"
                    className="venue-profile-toggle"
                  >
                    <img
                      src={user.picture}
                      alt="Profile"
                      className="venue-profile-image rounded-circle"
                      width="40"
                    />
                  </DropdownToggle>
                  <DropdownMenu style={{ right: 0, left: "auto" }}>
                    <DropdownItem header>{user.name}</DropdownItem>
                    <DropdownItem
                      tag={RouterNavLink}
                      to="/profile"
                      className="dropdown-profile"
                      activeClassName="active"
                    >
                      <FontAwesomeIcon icon="user" className="mr-3" /> Profil
                    </DropdownItem>
                    <DropdownItem id="qsLogoutBtn" onClick={logoutWithRedirect}>
                      <FontAwesomeIcon icon="power-off" className="mr-3" /> Çıkış Yap
                    </DropdownItem>
                  </DropdownMenu>
                </UncontrolledDropdown>
              ) : null}
            </Nav>

            <div className="venue-mobile-panel d-xl-none">
              {isAuthenticated ? (
                <>
                  <Nav className="venue-mobile-panel__actions" navbar>
                    {permissionNames.includes("manage_shift") ? (
                      <NavItem>{renderLink("/orders", "Siparişler", null, true)}</NavItem>
                    ) : null}
                    {canAccessTables ? (
                      <NavItem>{renderLink("/tables", "Masalar", null, true)}</NavItem>
                    ) : null}
                    {canAccessRequests ? (
                      <NavItem>
                        {renderLink("/requests", "Talepler", requestBadge, true)}
                      </NavItem>
                    ) : null}
                    {permissionNames.includes("take_payment") ? (
                      <NavItem>{renderLink("/cashier", "Kasa", null, true)}</NavItem>
                    ) : null}
                    {permissionNames.includes("manage_shift") ? (
                      <NavItem>{renderLink("/shift", "Vardiya", null, true)}</NavItem>
                    ) : null}
                  </Nav>

                  <div className="venue-mobile-panel__user">
                    <Button
                      id="venueSoundToggleBtnMobile"
                      color="link"
                      className="sound-toggle-button venue-icon-button position-relative"
                      onClick={handleSoundControl}
                      title={soundButtonTitle}
                      aria-label={soundButtonTitle}
                    >
                      <FontAwesomeIcon
                        icon={soundEnabled ? "volume-up" : "volume-mute"}
                        className="mr-2"
                      />
                      Bildirim sesi
                      {soundEnabled && playbackBlocked ? (
                        <span className="sound-toggle-indicator" />
                      ) : null}
                    </Button>

                    <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                      <img
                        src={user.picture}
                        alt="Profile"
                        className="venue-profile-image rounded-circle"
                        width="40"
                      />
                      <span>{user.name}</span>
                    </div>
                  </div>

                  <Nav className="venue-mobile-panel__actions" navbar>
                    <NavItem>
                      <NavLink
                        tag={RouterNavLink}
                        to="/profile"
                        className="venue-mobile-link"
                        activeClassName="active"
                        onClick={closeMobilePanel}
                      >
                        <FontAwesomeIcon icon="user" className="mr-2" />
                        Profil
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <button
                        type="button"
                        className="venue-mobile-link"
                        id="qsLogoutBtnMobile"
                        onClick={logoutWithRedirect}
                      >
                        <FontAwesomeIcon icon="power-off" className="mr-2" />
                        Çıkış Yap
                      </button>
                    </NavItem>
                  </Nav>
                </>
              ) : (
                <Nav navbar>
                  <NavItem className="w-100">
                    <Button
                      id="qsLoginBtnMobile"
                      color="primary"
                      block
                      onClick={() => loginWithRedirect({})}
                    >
                      Giriş Yap
                    </Button>
                  </NavItem>
                </Nav>
              )}
            </div>
          </Collapse>
        </Container>
      </Navbar>
    </div>
  );
};

export default NavBar;
